import { useIsMutating } from '@/api/hooks';
import {
  Box,
  Button,
  Centralizer,
  DetailsInfo,
  Loading,
  Text,
} from '@/components';
import { useTenantManagerPermission } from '@/features/authentication/_hooks/permission';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { datetimeFormat, isRS } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAllUsers,
  useOrganization,
  useUpdateOrganizationMember,
} from '../../hooks';
import { MembersOfOrganizationModal } from './MembersOfOrganizationModal';
import { OrganizationName } from './OrganizationName';

export const OrganizationDetailsPage: FC = () => {
  const { t } = useTranslation();
  const { id: organizationId } = useId();
  const { mutateAsync: updateOrganizationMember } =
    useUpdateOrganizationMember();
  const mutationCount = useIsMutating();

  const { openMessage } = useMessage();
  const [openModal, setOpenModal] = useState(false);

  const isProgressApiRequest = mutationCount > 0;

  const organization = useOrganization({ id: organizationId });
  const users = useAllUsers();
  const editPermission = useTenantManagerPermission(true);

  const toggleOpenModal = () => {
    setOpenModal((prev) => !prev);
  };

  const submitUpdateOrganization = async (joinedUserIds: string[]) => {
    try {
      await updateOrganizationMember({
        id: organizationId,
        userIds: joinedUserIds,
      });

      await Promise.all([
        openMessage('success', t('message-update-completed')),
        (async () => {
          toggleOpenModal();
        })(),
      ]);
    } catch {
      await openMessage('error', t('message-update-failed'));
    }
  };

  const hasData = organization.data && users.data;
  const isLoading = organization.isLoading || users.isLoading;
  if (isLoading || !hasData) {
    return <Loading />;
  }

  if (organization.isError) {
    openMessage('error', t('failed-fetch-organization'));
  }

  if (users.isError) {
    openMessage('error', t('failed-fetch-user'));
  }

  return (
    <Centralizer>
      <DetailsInfo
        items={[
          ...(!isRS()
            ? [
                {
                  label: t('organization-code'),
                  value: (
                    <Text
                      weight="regular"
                      text={organization.data.externalSystemCode}
                    />
                  ),
                },
              ]
            : []),
          {
            label: t('organization-control-code'),
            value: <Text weight="regular" text={organization.data.code} />,
          },
          {
            label: t('name'),
            value: (
              <OrganizationName
                name={organization.data.name}
                isIntegrated={organization.data.isIntegrated}
                isExcludedFromOptions={organization.data.isExcludedFromOptions}
              />
            ),
          },
          {
            label: t('parent-organization-name'),
            value: (
              <Text weight="regular" text={organization.data.upper?.name} />
            ),
          },
          {
            label: t('number-of-users'),
            value: (
              <Text weight="regular" text={organization.data.users.length} />
            ),
          },
          {
            label: t('role-name'),
            value: (
              <Text weight="regular" text={organization.data.role?.name} />
            ),
          },
          {
            label: t('start-fiscal-year'),
            value: <Text weight="regular" text={organization.data.startYear} />,
          },
          {
            label: t('end-fiscal-year'),
            value: <Text weight="regular" text={organization.data.endYear} />,
          },
          {
            label: t('created-datetime'),
            value: (
              <Text
                weight="regular"
                text={datetimeFormat({ datetime: organization.data.createdAt })}
              />
            ),
          },
          {
            label: t('updated-datetime'),
            value: (
              <Text
                weight="regular"
                text={datetimeFormat({ datetime: organization.data.updatedAt })}
              />
            ),
          },
        ]}
      />

      <Box marginTop={16}>
        <Button type="default" onClick={toggleOpenModal}>
          {t('member-of-organization')}
        </Button>
      </Box>

      <MembersOfOrganizationModal
        allUsers={
          organization.data.isIntegrated
            ? users.data
            : users.data.filter((d) => !d.isIntegrated)
        }
        joinedUsers={
          organization.data.isIntegrated
            ? organization.data.users
            : organization.data.users.filter((user) => !user.isIntegrated)
        }
        open={openModal}
        onOK={submitUpdateOrganization}
        onCancel={toggleOpenModal}
        isLoading={isProgressApiRequest}
        isIntegrated={organization.data.isIntegrated}
        onlyView={!editPermission.data}
      />
    </Centralizer>
  );
};
